// src/components/AdminUserRoles.jsx
// VERSION AVEC FETCH DIRECT (contournement CORS)

import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

export default function AdminUserRoles() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [processingUserId, setProcessingUserId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState({ type: '', message: '' });
  // URL de la fonction (toujours HTTPS)
  const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/+$/,''); 
  const fnUrl = `${supabaseUrl}/functions/v1/admin-reset-password`;
  console.log('[admin-reset-password] URL =', fnUrl);

  const ROLES = [
    { value: 'user', label: '👤 Utilisateur', color: 'bg-gray-100 text-gray-800' },
    { value: 'expert', label: '👨‍🎨 Expert', color: 'bg-blue-100 text-blue-800' },
    { value: 'family_office', label: '🏛️ Family Office', color: 'bg-purple-100 text-purple-800' },
    { value: 'team', label: '👥 Équipe', color: 'bg-green-100 text-green-800' },
    { value: 'admin', label: '⚙️ Admin', color: 'bg-red-100 text-red-800' }
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  async function fetchUsers() {
    try {
      setLoading(true);
      setError(null);

      // RPC admin-gated : joint profiles + auth.users → email réel inclus.
      const { data: rows, error: rpcError } = await supabase.rpc('admin_list_users');

      if (rpcError) throw rpcError;

      const combinedUsers = (rows || []).map(row => ({
        id: row.id,
        email: row.email || `Utilisateur ${row.id.substring(0, 8)}...`,
        created_at: row.created_at,
        role: row.role || 'user',
        first_name: row.first_name,
        last_name: row.last_name,
        company: row.company,
        approved: row.approved
      }));

      setUsers(combinedUsers);
    } catch (err) {
      console.error('❌ Erreur dans fetchUsers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRoleChange(userId, newRole) {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole, updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (error) throw error;
      
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, role: newRole } : u
      ));

      setFeedbackMessage({ type: 'success', message: '✅ Rôle mis à jour avec succès !' });
      setTimeout(() => setFeedbackMessage({ type: '', message: '' }), 3000);
    } catch (err) {
      console.error('❌ Erreur lors du changement de rôle:', err);
      setFeedbackMessage({ type: 'error', message: `❌ Erreur: ${err.message}` });
      setTimeout(() => setFeedbackMessage({ type: '', message: '' }), 5000);
    }
  }

  function generatePassword() {
    const length = 16;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
    password += '0123456789'[Math.floor(Math.random() * 10)];
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)];
    
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    return password.split('').sort(() => Math.random() - 0.5).join('');
  }

  function openPasswordModal(user) {
    setSelectedUser(user);
    setNewPassword(generatePassword());
    setShowPasswordModal(true);
    setError(null);
    setSuccessMessage('');
  }

async function updateUserPassword() {
  if (!selectedUser || !newPassword) return;

  if (newPassword.length < 8) {
    setError('Le mot de passe doit contenir au moins 8 caractères');
    return;
  }

  setProcessingUserId(selectedUser.id);
  setError(null);
  setSuccessMessage('');

  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (sessionError || !session) {
      throw new Error('Session invalide. Veuillez vous reconnecter.');
    }

    if (!supabaseUrl) {
      throw new Error('REACT_APP_SUPABASE_URL non configuré');
    }

    // ⚠️ Compat : on envoie aussi l'ancien format attendu par d'autres versions
    const payload = {
      user_id: selectedUser.id,
      newPassword: newPassword,
      target_user_id: selectedUser.id,
      new_password: newPassword
    };

      // Appel robuste : CORS explicite, pas de credentials, en-têtes REST Supabase
      const response = await fetch(fnUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || ''
        },
        body: JSON.stringify(payload)
      });

      // Lecture robuste de la réponse (JSON ou texte)
      let resultBody = null;
      let rawText = '';
      try {
        rawText = await response.text();
        resultBody = rawText ? JSON.parse(rawText) : {};
      } catch {
        resultBody = { raw: rawText };
      }
      console.log('📬 Edge function response:', response.status, resultBody);
 

      if (!response.ok || resultBody?.success === false) {
        const msg =
          resultBody?.error ||
          resultBody?.message ||
          resultBody?.msg ||
          (typeof resultBody === 'string' ? resultBody : '') ||
          `Erreur HTTP ${response.status}`;
        throw new Error(msg);
      }

    setSuccessMessage(
      `✅ Mot de passe mis à jour avec succès !\n\n` +
      `Nouveau mot de passe : ${newPassword}\n\n` +
      `⚠️ Communiquez ce mot de passe à l'utilisateur de manière sécurisée.`
    );

    if (navigator.clipboard) {
      navigator.clipboard.writeText(newPassword);
    }

    } catch (err) {
      console.error('❌ Erreur lors de la mise à jour du mot de passe:', err);
      // Cas réseau/CORS → message clair
      if (String(err).includes('Failed to fetch')) {
        setError('❌ Failed to fetch — vérifie AdBlock/ITP, HTTPS et les CORS de la fonction.');
      } else {
        setError(`❌ ${err?.message || 'Echec MAJ mot de passe'}`);
      }
    } finally {
      setProcessingUserId(null);
    }
  }

  function copyToClipboard(text) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setFeedbackMessage({ type: 'success', message: '✅ Copié dans le presse-papier !' });
      setTimeout(() => setFeedbackMessage({ type: '', message: '' }), 2000);
    }
  }

  function getRoleInfo(roleValue) {
    return ROLES.find(r => r.value === roleValue) || ROLES[0];
  }

  function getUserDisplayName(user) {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.first_name || user.company || 'Non défini';
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  if (error && !showPasswordModal) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <span className="text-2xl">❌</span>
          <div>
            <h3 className="font-semibold text-red-900 mb-2">Erreur de chargement</h3>
            <p className="text-sm text-red-700 mb-4">{error}</p>
            <button 
              onClick={fetchUsers}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
        <span className="text-6xl mb-4 block">👥</span>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun utilisateur trouvé</h3>
        <p className="text-gray-600">Il n'y a actuellement aucun utilisateur dans la base de données.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-bold text-gray-900 flex items-center gap-2">
            👥 Gestion des rôles utilisateurs
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {users.length} utilisateur{users.length > 1 ? 's' : ''} • Gérez les permissions et mots de passe
          </p>
        </div>
        <button 
          onClick={fetchUsers}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Actualiser
        </button>
      </div>

      {feedbackMessage.message && (
        <div className={`rounded-lg p-4 ${
          feedbackMessage.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {feedbackMessage.message}
        </div>
      )}

      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Rôles disponibles :</h3>
        <div className="flex flex-wrap gap-2">
          {ROLES.map(role => (
            <span key={role.value} className={`px-3 py-1.5 rounded-full text-xs font-medium ${role.color}`}>
              {role.label}
            </span>
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Utilisateur
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Rôle actuel
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Changer rôle
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => {
              const currentRole = getRoleInfo(user.role);
              return (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="text-sm text-gray-900 font-medium">
                          {getUserDisplayName(user)}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5 break-all">{user.email}</div>
                        {user.company && user.first_name && (
                          <div className="text-xs text-gray-500">{user.company}</div>
                        )}
                        <div className="text-xs font-mono text-gray-400 mt-1">{user.id.substring(0, 8)}...</div>
                      </div>
                      {!user.approved && (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                          En attente
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${currentRole.color}`}>
                      {currentRole.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {ROLES.map(role => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => openPasswordModal(user)}
                      disabled={processingUserId === user.id}
                      className="px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ml-auto"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                      Mot de passe
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {ROLES.map(role => {
          const count = users.filter(u => u.role === role.value).length;
          return (
            <div key={role.value} className={`${role.color} rounded-lg p-4`}>
              <div className="text-2xl font-bold">{count}</div>
              <div className="text-xs opacity-75 mt-1">{role.label}</div>
            </div>
          );
        })}
      </div>

      {/* Modal de confirmation */}
      {showPasswordModal && selectedUser && !successMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-serif font-bold text-gray-900">
                  ⚠️ Confirmation requise
                </h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUser(null);
                    setNewPassword('');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <h4 className="font-semibold text-amber-900 mb-2">Attention</h4>
                    <p className="text-sm text-amber-800">
                      Vous allez définir un nouveau mot de passe pour :<br />
                      <strong>{getUserDisplayName(selectedUser)}</strong>
                    </p>
                    <p className="text-sm text-amber-800 mt-2">
                      L'utilisateur devra utiliser ce mot de passe pour se connecter.
                    </p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">❌</span>
                    <div>
                      <h4 className="font-semibold text-red-900 mb-1">Erreur</h4>
                      <p className="text-sm text-red-800 whitespace-pre-wrap">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau mot de passe
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent font-mono text-sm"
                    placeholder="Minimum 8 caractères"
                  />
                  <button
                    onClick={() => setNewPassword(generatePassword())}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Générer
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-4">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Instructions importantes
                </h4>
                <ul className="text-sm text-blue-800 space-y-1 ml-7">
                  <li>• Le mot de passe sera copié automatiquement</li>
                  <li>• Communiquez-le par téléphone ou vidéo</li>
                  <li>• Demandez à l'utilisateur de le changer</li>
                  <li>• Ne le partagez JAMAIS par email non chiffré</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUser(null);
                    setNewPassword('');
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={updateUserPassword}
                  disabled={!newPassword || newPassword.length < 8 || processingUserId}
                  className="flex-1 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {processingUserId ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Mise à jour...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Confirmer le changement</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de succès */}
      {showPasswordModal && selectedUser && successMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-serif font-bold text-gray-900">
                  ✅ Succès
                </h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUser(null);
                    setNewPassword('');
                    setSuccessMessage('');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">✅</span>
                  <div className="flex-1">
                    <h4 className="font-semibold text-green-900 mb-2">Mot de passe mis à jour !</h4>
                    <p className="text-sm text-green-800 whitespace-pre-wrap font-mono bg-white p-3 rounded border border-green-200">
                      {newPassword}
                    </p>
                    <p className="text-sm text-green-800 mt-3">
                      ⚠️ Communiquez ce mot de passe à l'utilisateur de manière sécurisée (téléphone, vidéo).
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => copyToClipboard(newPassword)}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copier le mot de passe
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}